import { CONTACT_TYPES } from "@core/constants";
import { NativeCommunicationManager } from "@core/managers/native-communication.manager";
import { NotificationsManager } from "@core/managers/notifications.manager";
import { ScrollManager } from "@core/managers/scroll.manager";
import { RecruitmentCandidatesService } from "@core/services";
import { ETAB_ROUTES } from "@etab/config";
import { ACTION_RECRUITMENTS_SET_SELECTED_EXTRA, GETTER_RECRUITMENTS_GET_SELECTED_EXTRA } from "@etab/stores/types";
import { useRecruitmentsStore } from "@etab/stores/recruitments";
import { useRoute, useRouter } from "vue-router";
import { useQuasar } from "quasar";
import { useI18n } from "vue-i18n";

export function useRecruitmentCandidate() {
  const route = useRoute();
  const router = useRouter();
  const $q = useQuasar();
  const { t } = useI18n();

  const $_scrollToPreviouslyOpenedExtra = function () {
    const recruitmentsStore = useRecruitmentsStore();
    // get the selected recruitment (if any)
    const selectedExtraId = recruitmentsStore[GETTER_RECRUITMENTS_GET_SELECTED_EXTRA];
    if (selectedExtraId) {
      const selectedEl = document.getElementById(selectedExtraId);
      if (selectedEl) {
        // if we find one,we scroll to it
        ScrollManager.scrollToElement(selectedEl);
      }
      // we want this to be a one time access, in order to prevent weird behavior on navigation
      recruitmentsStore[ACTION_RECRUITMENTS_SET_SELECTED_EXTRA](undefined);
    } else {
      ScrollManager.scrollToTop();
    }
  };

  const $_goToProfileFromPermanentDemand = function ({ candidate }) {
    const candidateId = candidate.getId();
    const query = route.query;
    const demandId = route.params.demandId;
    const demandType = route.params.demandType;

    router
      .push({
        name: ETAB_ROUTES.dashboard.recruitment.permanent.candidate.root.name,
        params: { demandId, demandType, candidateId },
        query: { ...query },
      })
      .catch((suppressedError) => {});
  };

  const $_goToProfileFromTemporaryDemand = function ({ candidate }) {
    const candidateId = candidate.getId();
    const demandId = route.params.demandId;
    const demandType = route.params.demandType;
    router
      .push({
        name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.profile.name,
        params: { candidateId, demandType, demandId },
      })
      .catch((suppressedError) => {});
  };

  const $_goToMissionFromTemporaryDemand = function ({ candidate }) {
    const candidateId = candidate.getId();
    const demandId = route.params.demandId;
    const demandType = route.params.demandType;
    router
      .push({
        name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.mission.name,
        params: { candidateId, demandType, demandId },
      })
      .catch((suppressedError) => {});
  };

  const $_goToProfileFromMultiCandidatesMERDemand = function ({ candidate }) {
    const candidateId = candidate.getId();
    const demandId = route.params.demandId;
    const demandType = route.params.demandType;
    // in the case of MultiCandidate MER, the demandId can be different:
    const linkedId = candidate.getLinkedId();
    router
      .push({
        name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.mer.name,
        params: { candidateId, demandType, demandId, linkedId },
      })
      .catch((suppressedError) => {});
  };

  const $_onContactCandidate = function ({ candidate, contactCallback = () => {} }) {
    const { firstname, phone, email } = candidate.getIdentity();

    const actions = [];
    if (phone) {
      actions.push({
        label: t("ETAB.PAGES.RECRUITMENT.@PHONE_CALL"),
        icon: "icon-f-phone-call",
        id: CONTACT_TYPES.PHONE,
      });
      actions.push({
        label: t("ETAB.PAGES.RECRUITMENT.@SMS"),
        icon: "icon-f-message-square",
        id: CONTACT_TYPES.SMS,
      });
    }
    if (email) {
      actions.push({
        label: t("ETAB.PAGES.RECRUITMENT.@EMAIL"),
        icon: "icon-f-mail",
        id: CONTACT_TYPES.MAIL,
      });
    }

    if (actions.length > 0) {
      $q.bottomSheet({
        message: t("ETAB.PAGES.RECRUITMENT.@CONTACT_CANDIDATE_FIRSTNAME", { firstname }),
        actions,
      }).onOk((action) => {
        contactCallback({ candidate });
        switch (action.id) {
          case CONTACT_TYPES.PHONE: {
            NativeCommunicationManager.phone({ phone });
            break;
          }
          case CONTACT_TYPES.SMS: {
            NativeCommunicationManager.sms({ phone });
            break;
          }
          default:
          case CONTACT_TYPES.MAIL: {
            NativeCommunicationManager.sendEmail({ to: email });
            break;
          }
        }
      });
    }
  };

  const $_ignorePermanentCandidate = async function ({ candidate, demandId }) {
    const candidateId = candidate.getId();
    await RecruitmentCandidatesService.ignorePermanentCandidate({ demandId, candidateId });
    const message = t("SUCCESS.@CANDIDATE_IGNORED_WITH_NAME", { name: candidate.getFirstname() });
    NotificationsManager.success({ message });
  };

  const $_ignoreAECandidate = async function ({ candidate }) {
    const candidateId = candidate.getId();
    const demandId = route.params.demandId;
    await RecruitmentCandidatesService.ignoreAECandidate({ demandId, candidateId });
    const message = t("SUCCESS.@CANDIDATE_IGNORED_WITH_NAME", { name: candidate.getFirstname() });
    NotificationsManager.success({ message });
  };

  const $_ignoreMERCandidate = async function ({ candidate }) {
    const candidateId = candidate.getId();
    const demandId = route.params.demandId;
    await RecruitmentCandidatesService.ignoreMERCandidate({ demandId, candidateId });
    const message = t("SUCCESS.@CANDIDATE_IGNORED_WITH_NAME", { name: candidate.getFirstname() });
    NotificationsManager.success({ message });
  };

  const $_reportCandidate = async function ({ candidateId, demandId, reasonId, comment }) {
    await RecruitmentCandidatesService.reportPermanentCandidate({ candidateId, demandId, reasonId, comment });
    NotificationsManager.success({ message: t("SUCCESS.@CANDIDATE_REPORTED") });
  };

  return {
    $_scrollToPreviouslyOpenedExtra,
    $_goToProfileFromPermanentDemand,
    $_goToProfileFromTemporaryDemand,
    $_goToMissionFromTemporaryDemand,
    $_goToProfileFromMultiCandidatesMERDemand,
    $_onContactCandidate,
    $_ignorePermanentCandidate,
    $_ignoreAECandidate,
    $_ignoreMERCandidate,
    $_reportCandidate,
  };
}
